/* The review checklist (6.3) and the shortcut sheet (7.1.3), both as right
 * drawers. One file because they are the same shell with different contents,
 * and two files would duplicate the shell's escape handling and focus trap. */

import { useEffect } from "react";
import type { ReactNode } from "react";
import type { AutoCheck } from "../lib/checks";
import { Button } from "./ui";

function Drawer({ open, title, onClose, children, footer }: {
  open: boolean; title: string; onClose: () => void;
  children: ReactNode; footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex h-full w-[28rem] flex-col bg-card
        shadow-2xl ring-1 ring-line">
        <header className="flex items-center justify-between border-b
          border-line px-4 py-3">
          <h2 className="text-sm font-extrabold tracking-[-0.01em] text-ink">{title}</h2>
          <button onClick={onClose}
            className="rounded-chip px-2 text-muted hover:text-ink">✕</button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>
        {footer && (
          <footer className="border-t border-line px-4 py-3">{footer}</footer>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------- the review checklist */

interface ChecklistProps {
  open: boolean;
  onClose: () => void;
  checks: AutoCheck[];
  manualLabels: string[];
  manual: boolean[];
  onToggleManual: (i: number) => void;
  onSubmit: () => void;
  canSubmit: boolean;
  /** 6.3.5: from the queue the same drawer opens as guidance, with no live
   *  pass/fail — there is no open video to evaluate. */
  informational?: boolean;
}

export function ChecklistDrawer(p: ChecklistProps) {
  return (
    <Drawer
      open={p.open}
      onClose={p.onClose}
      title={p.informational ? "Customer guidelines" : "Review checklist"}
      // The informational footer's Close is `variant="primary"` — the same
      // control as Continue / Start review on the queue. It was a plain
      // secondary button, so the only action in the drawer looked less
      // clickable than the rows behind it.
      footer={p.informational ? (
        <Button variant="primary" className="w-full" onClick={p.onClose}>
          Close
        </Button>
      ) : (
        <div className="flex gap-2">
          <Button className="flex-1" onClick={p.onClose}>Keep reviewing</Button>
          <Button variant="primary" className="flex-1"
            disabled={!p.canSubmit} onClick={p.onSubmit}>
            Submit video
          </Button>
        </div>
      )}
    >
      {/* The drawer opened straight into a list of checks with no heading and
          no explanation, so the automatic ones read as an unexplained wall and
          only the manual ones were labelled — and labelled "CONFIRM BY HAND",
          which is instruction-to-self, not a section title a customer reads. */}
      {/* The opening paragraph is CUSTOMER GUIDELINES ONLY.
          On the working screen it explained blocking versus advisory checks —
          a distinction the annotator does not act on, since Submit is either
          enabled or it is not, and the row's own ✓ / ! / ✕ already says which
          is which. Removed at testing's request. The guidelines version stays:
          it is signed-off wording and its reader has no screen to look at. */}
      {p.informational && (
        <p className="mb-4 text-sm leading-relaxed text-muted">
          These checks run on every video before you can submit it. The first
          six are automatic — the tool verifies them live. The last two you
          confirm yourself while watching.
        </p>
      )}
      {/* ONE LIST, TWO STATES. Testing asked for the working screen's checklist
          to read as the same list the customer guidelines show — so the ITEMS
          are identical wording either side, and the only difference is that
          here each one carries its live ✓ / ! / ✕ and its colour. The
          per-item prose is gone from both: it restated the label in a longer
          sentence, so the two screens listed the same eight things and looked
          like two different documents. */}

      <h3 className="mb-2 text-xs font-extrabold uppercase tracking-[0.08em] text-muted">
        CHECKED AUTOMATICALLY
      </h3>
      <ul className="flex flex-col gap-2">
        {p.checks.map((c) => (
          <li key={c.id} className={`rounded-field px-3 py-2 ring-1 ${
            p.informational
              ? "bg-ground ring-line"
              : c.pass
                ? "bg-verdict-approved-bg ring-verdict-approved-line"
                : c.blocking
                  ? "bg-verdict-rejected-bg ring-verdict-rejected-line"
                  : "bg-verdict-flagged-bg ring-verdict-flagged-line"
          }`}>
            <div className="flex items-start gap-2">
              {!p.informational && (
                <span className="pt-0.5 text-sm">
                  {c.pass ? "✓" : c.blocking ? "✕" : "!"}
                </span>
              )}
              <div>
                {/* No "advisory — never blocks" annotation. It only ever
                    applied to the rejected-sub-tasks check, and the prototype
                    lists that one alongside the other five with no caveat —
                    a customer reading the guidelines should see six checks,
                    not five and a footnote. */}
                <p className="text-sm font-semibold text-ink">{c.label}</p>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <h3 className="mt-5 mb-2 text-xs font-extrabold uppercase tracking-[0.08em] text-muted">
        TO BE MANUALLY REVIEWED
      </h3>
      {/* The explanatory sentence is gone at testing's request. It said the
          reviewer confirms each one before submitting, which the checkboxes and
          the disabled Submit button already say — and said it in the drawer a
          customer reads, where "the reviewer" is a role they do not have. */}
      <ul className="flex flex-col gap-2">
        {p.manualLabels.map((label, i) => (
          <li key={label}>
            {/* font-semibold to match the automatic checks above. These are
                items in the same list, and the two halves were set at different
                weights — which read as heading-and-detail rather than as eight
                peers. */}
            <label className="flex cursor-pointer items-start gap-2 rounded-field
              bg-ground px-3 py-2 text-sm font-semibold text-ink ring-1 ring-line">
              <input
                type="checkbox"
                disabled={p.informational}
                checked={p.manual[i] ?? false}
                onChange={() => p.onToggleManual(i)}
                className="mt-0.5 accent-teal"
              />
              {label}
            </label>
          </li>
        ))}
      </ul>
    </Drawer>
  );
}

/* --------------------------------------------------------- shortcut sheet */

/* Labels are testing's wording, verbatim. Several are shorter than what was
 * here — "Approve sub-task" for what the key actually does, which is approve
 * AND advance — and that is their call to make: the sheet is a reminder of
 * which key does roughly what, not the specification of it. */
const SHORTCUTS: [string, string][] = [
  ["Space", "Play / pause"],
  ["A", "Approve sub-task"],
  ["R", "Reject sub-task"],
  ["E", "Edit description"],
  ["J / K", "Previous / next sub-task"],
  ["S", "Split the sub-task at the playhead"],
  ["M", "Merge this sub-task with the one after it"],
  ["← / →", "Move the video ±1s"],
  // Was "Move the video ±5s". The buttons it documents now step by sub-task
  // (testing, 21 Sep), and a shortcut sheet describing the old behaviour is a
  // bug report waiting to be filed.
  ["Shift + ← / →", "Previous / next sub-task"],
  [", / .", "Step one frame back / forward"],
  ["G", "Open the review checklist"],
  ["?", "Shortcuts menu"],
  ["Esc", "Close open pop-ups or leave the field you are typing in"],
];

export function ShortcutSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Drawer open={open} onClose={onClose} title="Keyboard shortcuts">
      {/* Description LEFT, key RIGHT — the prototype's `shrow` layout. Keys
          are short and of wildly different width ("A" against "Shift + ← / →"),
          so a left key column leaves a ragged gutter and the descriptions never
          line up. With the keys right-aligned against the edge, the sentences
          start on one margin and the keys end on another. */}
      <dl className="flex flex-col">
        {SHORTCUTS.map(([key, what]) => (
          <div key={key}
            className="flex items-baseline justify-between gap-4 border-b
              border-line2 py-1.5 last:border-0">
            <dd className="text-sm text-text2">{what}</dd>
            <dt className="shrink-0 rounded-key border border-line bg-ground px-1.5
              py-0.5 font-mono text-[11px] text-text2">
              {key}
            </dt>
          </div>
        ))}
      </dl>

      <div className="mt-6 space-y-3 text-xs text-muted">
        <div>
          <p className="font-semibold text-text2">Split</p>
          <p>
            Cuts one sub-task in two at the orange line. The first half ends
            there, the second begins there and starts as a copy of the labels
            waiting to be corrected. Both go back to pending.
          </p>
        </div>
        <div>
          <p className="font-semibold text-text2">Merge</p>
          <p>
            Absorbs the next sub-task into this one. The survivor keeps its own
            labels — nothing is stitched together — and goes back to pending.
          </p>
        </div>
        <div>
          <p className="font-semibold text-text2">Why can't I move a task's edge?</p>
          <p>
            Task boundaries are fixed in this version. If the AI split the video
            into the wrong tasks, reject the sub-task with
            "task segmentation wrong" and it goes back with that reason.
          </p>
        </div>
      </div>
    </Drawer>
  );
}
